import { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { todayISO } from '../utils/dates';

import DateInput from './DateInput';
import { formatDate } from '../utils/dateFormat';
/** The opening stock part of a material's form: where it is held, how much, at what cost, and the cut-over date if none is set yet. */
export const emptyOpeningStock = () => ({ on: false, location: '', quantity: '', unitCost: '', lotNumber: '', expiryDate: '', date: '' });

const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};

/** Loads what the section needs: the cut-over date (if set) and the places that can hold stock. Org Admin and Finance Manager only. */
export function useOpeningStockInfo(active) {
  const [info, setInfo] = useState({ date: null, locations: [], loaded: false, allowed: true });
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    Promise.all([axios.get('/api/opening-balances'), axios.get('/api/opening-balances/options')])
      .then(([overview, options]) => {
        if (cancelled) return;
        setInfo({ date: overview.data.openingDate || null, locations: options.data.locations || [], loaded: true, allowed: true });
      })
      .catch(() => { if (!cancelled) setInfo({ date: null, locations: [], loaded: true, allowed: false }); });
    return () => { cancelled = true; };
  }, [active]);
  return [info, setInfo];
}

const locationKey = (l) => `${l.type}:${l.id}`;

/** What is missing from the opening stock, or '' when it can be saved (or is not wanted). Checked before anything is saved. */
export function openingStockProblem(form, info, unitOfMeasurementId) {
  if (!form.on) return '';
  if (!unitOfMeasurementId) return 'Choose the unit of measurement for the opening stock';
  if (!form.location) return 'Choose where the opening stock is held';
  if (num(form.quantity) <= 0) return 'Enter the opening stock quantity';
  if (form.unitCost === '' || num(form.unitCost) < 0) return 'Enter the cost per unit of the opening stock';
  if (!info.date && !form.date) return 'Choose the date your opening stock is as at';
  return '';
}

/** Sets the cut-over date when there is none, then books the stock through the opening balances (the same rules as Accounts > Opening Balances). */
export async function saveOpeningStock(form, info, itemId, unitOfMeasurementId) {
  if (!info.date) {
    await axios.put('/api/opening-balances/date', { date: form.date });
  }
  const [type, id] = form.location.split(':');
  await axios.post('/api/opening-balances/stock', {
    locationType: type,
    locationId: Number(id),
    itemId,
    unitOfMeasurementId: Number(unitOfMeasurementId),
    quantity: num(form.quantity),
    unitCost: num(form.unitCost),
    lotNumber: form.lotNumber.trim() || null,
    expiryDate: form.expiryDate || null
  });
}

function OpeningStockFields({ form, onChange, info, unitLabel, currency }) {
  const { expiryEnabled } = useAuth();
  // One place to hold stock needs no choosing
  useEffect(() => {
    if (form.on && !form.location && info.locations.length === 1) onChange({ ...form, location: locationKey(info.locations[0]) });
  }, [form, info.locations, onChange]);

  if (info.loaded && !info.allowed) return null;
  const value = Math.round((num(form.quantity) * num(form.unitCost) + Number.EPSILON) * 100) / 100;

  return (
    <div className="border rounded p-3 mb-3">
      <Form.Check
        type="checkbox"
        id="material-opening-stock"
        label="I already have stock of this item (opening stock)"
        checked={form.on}
        onChange={(e) => onChange({ ...form, on: e.target.checked })}
      />
      {form.on && (
        <div className="mt-3">
          {info.locations.length === 0 ? (
            <p className="text-muted small mb-0">
              Add a warehouse, retail store or production unit first (Org Admin), so there is a place to hold the stock.
            </p>
          ) : (
            <>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Held at</Form.Label>
                    <Form.Select value={form.location} onChange={(e) => onChange({ ...form, location: e.target.value })}>
                      <option value="">Choose where</option>
                      {info.locations.map((l) => <option key={locationKey(l)} value={locationKey(l)}>{l.name}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Lot or batch number (optional)</Form.Label>
                    <Form.Control maxLength={50} value={form.lotNumber} onChange={(e) => onChange({ ...form, lotNumber: e.target.value })} />
                  </Form.Group>
                </Col>
              </Row>
              {expiryEnabled && (
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Expiry date (optional)</Form.Label>
                      <DateInput value={form.expiryDate || ''} onChange={(e) => onChange({ ...form, expiryDate: e.target.value })} />
                    </Form.Group>
                  </Col>
                </Row>
              )}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Quantity{unitLabel ? ` (${unitLabel})` : ''}</Form.Label>
                    <Form.Control type="number" min="0" step="any" value={form.quantity} onChange={(e) => onChange({ ...form, quantity: e.target.value })} />
                    {!unitLabel && <Form.Text className="text-warning">Choose the unit of measurement below first.</Form.Text>}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cost per unit{currency ? ` (${currency})` : ''}</Form.Label>
                    <Form.Control type="number" min="0" step="any" value={form.unitCost} onChange={(e) => onChange({ ...form, unitCost: e.target.value })} />
                  </Form.Group>
                </Col>
              </Row>
              {info.date ? (
                <p className="text-muted small mb-0">
                  Counted as at {formatDate(info.date)}{value > 0 ? `; worth ${currency ? `${currency} ` : ''}${value.toFixed(2)}` : ''}. This is the date your opening balances are as at,
                  set once for everything (Accounts &rsaquo; Opening Balances).
                </p>
              ) : (
                <Form.Group className="mb-0">
                  <Form.Label>Opening stock is as at</Form.Label>
                  <DateInput max={todayISO()} style={{ maxWidth: 200 }} value={form.date}
                    onChange={(e) => onChange({ ...form, date: e.target.value })} />
                  <Form.Text className="text-muted">
                    The day your stock count is as of, usually the day before you start using iBigDo. It is set once for all your opening
                    balances and cannot be changed after any are entered.
                  </Form.Text>
                </Form.Group>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default OpeningStockFields;
